import type { IBetLimits, ILimit } from '../../../store/slices/bets/types';

export type ParseBetLimitsType = (result: IServerTableConfigResponseParams) => IBetLimits;
export type ParsePayoutsType = (result: IServerTableConfigResponseParams) => PayoutType[];

export interface ITableConfigParserOptions {
  parseBetLimits: ParseBetLimitsType;
  parsePayouts?: ParsePayoutsType;
}

export interface PayoutType {
  code: number;
  payout: number;
}

export interface IServerAutoplayConfig {
  roundsCount?: number[];
  roundStepper?: number[];
  maxRounds?: number;
}

export enum WinMessageBranding {
  ONLY_PP_BRANDING = `onlyppbranding`,
  ONLY_TABLE_NAME = `onlytablename`,
  TOTAL_BRANDING = `totalbranding`,
  NONE = `none`,
}

export interface Jurisdiction {
  disableChat?: boolean;
  showTableLimitsPopup?: boolean;
}

export interface TableConfigType {
  aamsSessionId: string | null;
  aamsTicket: string | null;
  betCode?: Record<string | number, string | number>;
  betLimits: IBetLimits;
  bettingTime: number;
  chipAmounts: number[];
  chipColorCodes: string[];
  dedicatedLanguage: string | null;
  defaultChipOrder: number;
  enableVideoStats: boolean;
  gameStatsAddress: string;
  gameTypeId: number;
  gsAddress: string;
  hlsLocation: string;
  hideLobby: boolean;
  idleLimit: number;
  isPrizeDropBetDataSendEnabled: boolean;
  isPromoBetDataSendEnabled: boolean;
  openHours: string | null;
  openTime: number;
  operatorGameId: string;
  operatorTheme: string;
  payoutBetMaxLimit?: number;
  euroTablePayoutMax?: number;
  tablePayoutMax?: number;
  payouts: PayoutType[];
  retrieveVideoQuality: boolean;
  sessionLaunchTimePerTable: string | null; //TODO: check possibility of using timestamp
  storeVideoQuality: boolean;
  subtitlesAvailable?: boolean;
  subtitle: string[];
  tableBetLimit: ILimit;
  tableClosed: boolean;
  tableName: string;
  tableType: string;
  videoLocation: string;
  videoPlayerVersion?: number;
  wsAddress: string;
  winMessageBranding: WinMessageBranding;
  broadcasterWsAddress?: string;
  broadcasterEnabled: boolean;
  chatVisibleByDefault?: boolean;
  isInfiniteAutoPlayEnabled?: boolean;
  maxMultiplier?: number;
  perPayoutBetMaxLimit?: number;
  customMultiplierCap?: number;
  canvasEnabled?: boolean;
  chatDisabled?: boolean;
  tableVariant?: string;
  showPPBranding?: boolean;
  hasNoCommissionToggle?: boolean;
  audioMode?: string | null;
  megaBaccaratMegaFeePercent?: number;
  disableChineseChar?: string;
  disableSideBets?: string;
  operatorMusic?: string;
  multiSeat?: boolean;
  useChroma?: boolean;
  themeHslaColorCode?: string;
  multiSeatLimit?: number;
  maxConcurrentPlayers?: number;
  sidebets?: boolean;
  betbehind?: boolean;
  tableCategory?: string;
  perfectpairs_max_bet?: number;
  perfectPairsMinBet?: number;
  perfectPairsMaxBet?: number;
  twentyOne3MinBbet?: number;
  twentyOne3MaxBet?: number;
  betBehindMinBet?: number;
  betBehindMaxBet?: number;
  dealNow?: boolean;
  dealNowMax?: number;
  disableBJVideoPanning?: boolean;
  bgMusic?: boolean;
  defaultBetAmount?: number;
  jurisdiction?: Jurisdiction;
  autoplayConfig?: IServerAutoplayConfig;
  minBalanceToPlay?: number;
}

export interface IServerTableConfigResponseParams {
  aamsSessionId: string | null;
  aamsTicket: string | null;
  betting_time: number;
  broadcaster_ws: string;
  chip_amounts: string;
  chip_color_codes: string | string[];
  default_chip_order: number;
  dedicatedLanguage: string | null;
  enable_video_stats: boolean;
  gamestats_address: string;
  gametype_id: number;
  gs_address: string;
  hls_location: string;
  hide_lobby: boolean;
  idle_limit_games: number;
  isPrizeDropBetDataSendEnabled: boolean;
  isPromoBetDataSendEnabled: boolean;
  openTime: number;
  open_hours: string;
  operator_theme: string;
  retrive_video_quality: boolean;
  session_launch_time_per_table: string | null;
  store_video_quality: boolean;
  table_bet_max_limit: number;
  table_bet_min_limit: number;
  subtitles_available?: boolean;
  subtitle?: string;
  table_category: string;
  table_closed: boolean;
  table_type: string;
  video_location: string;
  video_player_version: number;
  ws_address: string;
  youwinfeature: WinMessageBranding; // saved as winMessageBranding in store
  broadcaster_enabled: boolean;
  chatVisibleByDefault: boolean;
  isInfiniteAutoPlayEnabled: boolean;
  maxMultiplier: number;
  customMultiplierCap: number;
  canvasEnabled?: boolean;
  chatDisabled?: boolean;
  tableVariant?: string;
  hasNoCommissionToggle?: boolean;
  audioMode?: string | null;
  megaBaccaratMegaFeePercent?: number;
  disable_chinese_char?: string;
  disableSideBets?: string;
  operator_music?: string;
  bg_music?: boolean;
  autoplay?: IServerAutoplayConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface IServerTableConfigResponse {
  betCode: Record<string | number, string | number>;
  operatorGameId: string;
  params: IServerTableConfigResponseParams;
  tableId: string;
  tableName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}
